import { useState, useEffect, useRef } from "react";
import { Upload, X, Image, Video, FolderOpen, Maximize2 } from "lucide-react";

import {
  Button,
  Card,
  Typography,
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
} from "@mui/material";
import SocialOperator from "../../Social/SocialOperator";
import { URLS } from "../../../constants";
import { uploader } from "../../../utils/uploader";
import { MEDIA_COLLECTIONS } from "../../../utils/defaultSettings";
import ImageEditor from "../../Social/SocialOperator/ImageEditor";
import VideoTrimmer from "../../../components/VideoTrimmer/VideoTrimmer";
import {
  dataURItoBlob,
  imageDataToDataURL,
} from "../../../utils/mediaFunctions";

const MediaLibrary = ({ onMediaSelect, eventId, loggedInUserObj }) => {
  const fileInput = useRef(null);
  const [libraryFiles, setLibraryFiles] = useState([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(6);
  const [refresh, setRefresh] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxContent, setLightboxContent] = useState(null);
  const [editorTimings, setEditorTimings] = useState([]);
  const imageEditorRef = useRef(null);
  const [updatedVideo, setUpdatedVideo] = useState(null);
  const openLightbox = (file) => {
    setLightboxContent(file);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxContent(null);
  };

  useEffect(() => {
    const fetchMediaLibraryFiles = async () => {
      if (selectedCollection === 6 && eventId) {
        try {
          const response = await fetch(
            `${URLS.GET_MEDIA_LIBRARY_FILES(eventId)}`,
            {
              headers: {
                token: `${loggedInUserObj.token}`,
              },
              method: "GET",
            }
          );
          if (response.ok) {
            const data = await response.json();
            if (data && data.mediaFiles && data.mediaFiles.length > 0) {
              setLibraryFiles([...data.mediaFiles].reverse());
            } else {
              setLibraryFiles([]);
            }
          } else {
            console.error(
              "Failed to fetch media library files:",
              response.statusText
            );
            setLibraryFiles([]);
          }
        } catch (error) {
          console.error("Error fetching media library files:", error);
          setLibraryFiles([]);
        }
      } else {
        setLibraryFiles([]);
      }
    };

    fetchMediaLibraryFiles();
  }, [selectedCollection, eventId, refresh]);

  const uploadFile = async (fileBlob, type) => {
    const payload = {
      comment: "Media Library Upload",
      eventId: eventId,
      userId: "", // This might need to be dynamic based on logged-in user
      isMediaLibrary: true,
      mediaType: fileBlob?.type || type,
    };

    payload["mediaFile"] = fileBlob;

    const response = await uploader({
      url: URLS.UPLOAD_MEDIA(),
      body: payload,
    });
    setRefresh((prev) => !prev);
    return response;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    processFiles(files);
  };

  const handleFileInput = (e) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      processFiles(files);
    }
  };

  const processFiles = async (files) => {
    const validFiles = files.filter(
      (file) => file.type.startsWith("image/") || file.type.startsWith("video/")
    );

    const uploadPromises = validFiles.map(async (file) => {
      const type = file.type.startsWith("image/") ? "image" : "video";
      const response = await uploadFile(file, type);
      if (response.status === 201 && response.sharedMedia) {
        return response.sharedMedia;
      } else {
        console.error("Failed to upload file:", file.name, response);
        return null;
      }
    });

    const uploadedResults = await Promise.all(uploadPromises);
    const newUploadedFiles = uploadedResults.filter((file) => file !== null);

    setLibraryFiles((prev) => [...prev, ...newUploadedFiles]);
  };

  const removeFile = async (id) => {
    const fileToRemove = libraryFiles.find((file) => file._id === id);
    if (fileToRemove && fileToRemove._id) {
      try {
        const response = await fetch(`${URLS.DELETE_MEDIA(fileToRemove._id)}`, {
          method: "DELETE",
          headers: {
            token: `${loggedInUserObj.token}`,
          },
        });
        if (response.ok) {
          setLibraryFiles((prev) => prev.filter((file) => file._id !== id));
        } else {
          console.error("Failed to delete media file:", response.statusText);
        }
      } catch (error) {
        console.error("Error deleting media file:", error);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };
  const onSaveImage = async () => {
    if (imageEditorRef.current) {
      const imageData = imageEditorRef.current.getImageData();
      if (imageData) {
        const imageDataUrl = imageDataToDataURL(imageData);
        const blob = dataURItoBlob(imageDataUrl);
        await uploadFile(blob, "image/jpeg");
      }
    }
    closeLightbox();
  };
  const renderImageReviewer = (media) => {
    return (
      <ImageEditor
        onSaveImage={onSaveImage}
        ref={imageEditorRef}
        media={media}
        showApproveButton={true}
        style={{
          position: "absolute",
          top: 66,
          right: 28,
        }}
      />
    );
  };
  const updatedVideoBlob = async (newFile) => {
    if (newFile?.blob) {
      await uploadFile(newFile.blob, "video/mp4");
    }
    closeLightbox();
  };

  const renderVideoEditor = (media) => {
    return (
      <VideoTrimmer
        videoUrl={media?.mediaFileUrl}
        showControls={true}
        timings={editorTimings}
        setTimings={setEditorTimings}
        hasVideoPoster={media?.videoPosterUrl ? true : false}
        updatedVideoBlob={updatedVideoBlob}
      />
    );
  };

  return (
    <Card className="p-4 bg-gray-800 border-gray-700 h-full flex flex-col uv-card">
      <div className="flex items-center gap-2 mb-4">
        <FolderOpen className="w-5 h-5 text-teal-400" />
        <h2 className="text-lg font-semibold text-white">Media Library</h2>
      </div>

      {/* Upload Area - Fixed height */}
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        className={`
          border-2 border-dashed rounded-lg p-4 text-center transition-all duration-200 mb-4 flex-shrink-0 h-24
          ${
            isDragOver
              ? "border-teal-400 bg-teal-900/20"
              : "border-gray-600 hover:border-teal-500 hover:bg-gray-700/50"
          }
        `}
      >
        <Upload className="w-6 h-6 text-gray-400 mx-auto mb-2" />
        <p className="text-xs text-gray-400 mb-2">Drop files to save</p>
        <label className="cursor-pointer">
          <Button
            variant="outline"
            size="sm"
            className="!bg-gray-700 !border-gray-600 text-gray-300 !hover:bg-gray-600 !hover:border-gray-500"
            onClick={(e) => {
              e.preventDefault();
              fileInput.current.click();
            }}
          >
            Browse
          </Button>
          <input
            ref={fileInput}
            type="file"
            multiple
            accept="image/*,video/*"
            onChange={handleFileInput}
            className="hidden"
          />
        </label>
      </div>
      <div>
        <Typography
          htmlFor="aspect-ratio"
          className="text-sm font-small text-gray-200 !mb-1 !mt-2"
        >
          Select Media Collection
        </Typography>
        <select
          value={selectedCollection}
          onChange={(e) => setSelectedCollection(Number(e.target.value))}
          className="cursor-pointer w-full p-2 bg-gray-700 border border-gray-600 rounded text-white"
        >
          {MEDIA_COLLECTIONS.map((collection) => (
            <option
              key={collection.id}
              value={collection.id}
              className="cursor-pointer"
            >
              {collection.name}
            </option>
          ))}
        </select>
      </div>

      {/* Library Files - Fixed height container with internal scrolling */}
      <div className="flex-1 flex flex-col min-h-0">
        {selectedCollection == 6 && libraryFiles.length > 0 ? (
          <>
            <div className="flex items-center justify-center my-3 flex-shrink-0">
              <p className="text-sm text-gray-300">
                Total Files ({libraryFiles.length})
              </p>
            </div>

            {/* Fixed height scrollable area - exactly 12 items (6 rows × 2 columns) */}
            <div className="flex-1 min-h-0">
              <div className="media-library-uploads-container">
                <div className="grid grid-cols-2 gap-2">
                  {libraryFiles.map((file) => (
                    <div
                      key={file.id}
                      className="relative group"
                      onDragStart={(e) =>
                        e.dataTransfer.setData(
                          "text/plain",
                          JSON.stringify({
                            comment: "Media Library Upload",
                            mediaFileUrl: file?.mediaFileUrl,
                            mimeType: file?.mimeType,
                            id: file?._id,
                            videoPosterUrl: file?.videoPosterUrl || "",
                          })
                        )
                      }
                    >
                      <div
                        className={`flex justify-center items-center aspect-square rounded overflow-hidden bg-gray-700 border-2 cursor-pointer transition-all`}
                        onClick={() => openLightbox(file)}
                      >
                        {file.mimeType.includes("image") ? (
                          <img
                            src={file.mediaFileUrl}
                            alt="Media library file"
                            draggable={true}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error("Image failed to load:", file._id);
                              e.currentTarget.style.display = "none";
                            }}
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-gray-800">
                            <video
                              src={file.mediaFileUrl}
                              alt="Media library file"
                              draggable={true}
                              className="w-full h-full object-cover"
                              controls
                              onError={(e) => {
                                console.error(
                                  "Image failed to load:",
                                  file._id
                                );
                                e.currentTarget.style.display = "none";
                              }}
                              poster={file.videoPosterUrl || ""}
                            />
                          </div>
                        )}
                        <div className="absolute bg-black/0 group-hover:bg-black/20 transition-all duration-200 flex items-center justify-center">
                          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-gray-900/80 rounded-full p-2">
                            <Maximize2 className="w-5 h-5 text-white" />
                          </div>
                        </div>
                        {/* File type indicator */}
                        <div className="absolute top-1 left-1">
                          <div className="bg-black bg-opacity-60 rounded px-1 py-0.5 flex items-center space-x-1">
                            {file.mimeType.includes("image") ? (
                              <Image className="w-2 h-2 text-white" />
                            ) : (
                              <Video className="w-2 h-2 text-white" />
                            )}
                          </div>
                        </div>

                        {/* Remove button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(file._id);
                          }}
                          className="cursor-pointer absolute top-1 right-1 bg-red-500 hover:bg-red-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>

                      {/* File name */}
                      <p
                        className="text-xs text-gray-500 mt-1 truncate"
                        title={file._id}
                      >
                        {file._id}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex h-full mt-4 uv-ai-studio">
            <SocialOperator
              key={selectedCollection}
              forceRerender={() => {}}
              draggableMedia={true}
              dropdownSelection={true}
              controllable={true}
              activeSelectPanel={selectedCollection}
              activeTab={selectedCollection < 6 ? selectedCollection - 1 : 0}
              columnCount={2}
            />
          </div>
        )}
      </div>
      <Dialog
        open={lightboxOpen}
        onClose={closeLightbox}
        className="ai-result-lightbox"
      >
        <DialogContent className="max-w-4xl w-[80vw] h-[80vh] p-4 bg-black border-gray-700 overflow-hidden flex flex-col">
          <DialogTitle className="text-white text-center">
            Media Preview
          </DialogTitle>

          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-50 bg-gray-900/80 hover:bg-gray-800 text-white rounded-full p-2 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Media content - properly contained */}
          <div className="flex-1 flex items-center justify-center min-h-0">
            {lightboxContent &&
              (lightboxContent.mimeType?.includes("video")
                ? renderVideoEditor(lightboxContent)
                : renderImageReviewer(lightboxContent))}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};
export default MediaLibrary;
